import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useStore } from '../../store';
import {
  chatKeys,
  flattenMessages,
  lastFetchedSeq,
  useChannel,
  useMessages,
} from './useChatQueries';
import {
  useMarkRead,
  usePendingSends,
  useSendMessage,
} from './useChatMutations';
import { useAttachmentUpload } from './useAttachmentUpload';
import { useAttachmentUrls } from './useAttachmentUrls';
import { useEventsConnectionState } from './useChatEvents';
import { acceptFiles, FOLDER_NOTICE } from '../../utils/attachments';
import { getHttpStatus } from '../../utils/httpErrorUtils';
import { getBusinessErrorMessage } from '../../utils/apiErrorDetail';
import { encodeMentions } from '../../components/chat/mentions';

const MAX_MESSAGE_CHARS = 4000;
const MENTION_NOT_MEMBER = 'You can only mention members of this conversation.';

/** One composer per conversation: two windows on screen must not share an id. */
export function composerInputId(channelId: string): string {
  return `chat-send-${channelId}`;
}

export function useConversation(
  channelId: string,
  options: { onGone: () => void }
) {
  // Latest-ref: callers pass inline arrows, and a new identity must not re-run
  // the 404 path (toast, inbox refetch) while the same 404 stays cached.
  const onGoneRef = useRef(options.onGone);
  onGoneRef.current = options.onGone;
  const draft = useStore(state => state.chatDrafts[channelId] ?? '');
  const setChatDraft = useStore(state => state.setChatDraft);
  const setChatDraftMentions = useStore(state => state.setChatDraftMentions);
  const pending = usePendingSends(channelId);
  const showError = useStore(state => state.showError);

  const { data: header, error: headerError } = useChannel(channelId);
  const messagesQuery = useMessages(channelId);
  const messages = flattenMessages(messagesQuery.data?.pages);
  // One call for the whole page: one batch request and one audit row per file.
  const { urlsFor, download } = useAttachmentUrls();
  const pageAttachments = messages.flatMap(m => m.attachments ?? []);
  const urls = urlsFor(
    pageAttachments.map(a => a.id),
    pageAttachments.filter(a => a.status === 'ready').map(a => a.id)
  );
  const { send } = useSendMessage();
  const markRead = useMarkRead();
  const queryClient = useQueryClient();
  const [archivedOnSend, setArchivedOnSend] = useState(false);
  // The one notice line in the send bar: picker, drop, paste and the upload
  // URL's 422 all write here, and it clears after 3.5 s (spec, Attach).
  const [notice, setNotice] = useState<string | null>(null);
  useEffect(() => {
    if (!notice) return;
    const timer = setTimeout(() => setNotice(null), 3500);
    return () => clearTimeout(timer);
  }, [notice]);
  const upload = useAttachmentUpload(channelId, setNotice);
  const { chips, addFiles, clearSubmitted, doneIds } = upload;
  // 'stopped' is the state ConnectionStrip calls offline.
  const offline = useEventsConnectionState() === 'stopped';

  const archived = archivedOnSend || header?.archived === true;
  // Any other load failure: say so rather than claim the channel is empty or
  // that only announcers may post.
  const loadError = [headerError, messagesQuery.error].find(
    error => error && getHttpStatus(error) !== 404
  );
  // The same condition that renders SendBar.
  const canWrite = !loadError && !archived && header?.can_write === true;

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    // No send bar, no attach: a chip would be invisible and its upload an
    // orphan. Offline disables every route in, as it disables the plus.
    if (!canWrite || offline) return;
    const items = Array.from(event.dataTransfer.items ?? []);
    if (items.some(item => item.webkitGetAsEntry?.()?.isDirectory)) {
      setNotice(FOLDER_NOTICE);
      return;
    }
    addFiles(
      acceptFiles(
        Array.from(event.dataTransfer.files ?? []),
        chips.length,
        setNotice
      )
    );
  };

  // A paste in the textarea bubbles here, so SendBar needs no handler.
  const handlePaste = (event: React.ClipboardEvent) => {
    if (!canWrite || offline) return;
    const pasted = Array.from(event.clipboardData.files ?? []);
    if (pasted.length === 0) return;
    addFiles(acceptFiles(pasted, chips.length, setNotice));
  };

  // The channel was archived away, or membership was lost: let the caller leave
  // rather than show an empty header over an empty list.
  const leaveGone = useCallback(() => {
    onGoneRef.current();
    showError('This conversation is no longer available.');
    void queryClient.invalidateQueries({ queryKey: chatKeys.inbox() });
  }, [showError, queryClient]);

  useEffect(() => {
    if (
      getHttpStatus(headerError) === 404 ||
      getHttpStatus(messagesQuery.error) === 404
    ) {
      leaveGone();
    }
  }, [headerError, messagesQuery.error, leaveGone]);

  useEffect(() => {
    if (!loadError) return;
    console.error('Failed to load conversation', loadError);
    showError(
      'Failed to load conversation',
      getBusinessErrorMessage(loadError) ?? undefined
    );
  }, [loadError, showError]);

  // Mark read when the channel opens and when history arrives. Keyed on the
  // seq, not the array: `flattenMessages` returns a new array each render. The
  // seq is the newest fetched one, so a row that only a send response cached
  // cannot mark an unfetched earlier message read.
  const readSeq = lastFetchedSeq(queryClient, channelId, messages);
  useEffect(() => {
    if (!channelId || readSeq === 0) return;
    if (document.visibilityState !== 'visible') return;
    markRead(channelId, readSeq);
  }, [channelId, readSeq, markRead]);

  const doSend = async (draftBody: string, clientId?: string) => {
    let body = draftBody;
    // First attempt: the done chips. Retry: the ids that attempt recorded on
    // its mutation, never whatever the composer holds now.
    const attachmentIds = clientId
      ? (pending.find(p => p.client_id === clientId)?.attachment_ids ?? [])
      : doneIds;
    // Clear before the request, so a second Enter while it runs has nothing to
    // send. A transient failure stays visible as its pending row with Retry.
    if (!clientId) {
      // The server counts the trimmed body in code points. Check here, before
      // anything is cleared, so the draft and the chips stay.
      body = encodeMentions(
        draftBody,
        useStore.getState().chatDraftMentions[channelId] ?? []
      ).trim();
      if ([...body].length > MAX_MESSAGE_CHARS) {
        setNotice('Message is too long');
        return;
      }
      setChatDraft(channelId, '');
      setChatDraftMentions(channelId, []);
      // A chip whose upload failed is not in `doneIds`, so the send silently
      // drops it. The chip stays in the bar with Try again, but say so: the
      // person has already pressed Enter and believes the file went.
      // Name the files: with several chips in the bar, "the file" names none.
      const failed = chips
        .filter(chip => chip.state === 'failed')
        .map(chip => chip.fileName);
      if (failed.length === 1) {
        setNotice(`Sent without ${failed[0]}. Try again or remove it.`);
      } else if (failed.length > 1) {
        setNotice(
          `Sent without ${failed.length} files: ${failed.join(', ')}. Try again or remove them.`
        );
      }
      clearSubmitted(attachmentIds);
    }
    try {
      await send(channelId, body, clientId, attachmentIds);
    } catch (error) {
      const status = getHttpStatus(error);
      if (status === 404) {
        leaveGone();
        return;
      }
      if (status === 409 || status === 403) {
        // `send` has dropped the failed row, kept the text as the draft and
        // refetched the header. 409: replace the bar now, before that refetch.
        // The chips are not restored: their rows stay pending and the hourly
        // sweep removes them.
        if (status === 409) setArchivedOnSend(true);
        return;
      }
      if (status === 422) {
        // A file the server would not link (expired, swept, never uploaded):
        // a Retry resends the same ids and fails again, so `send` dropped the
        // row and kept the text; the person attaches the files again.
        const message =
          getBusinessErrorMessage(error) ??
          'The files could not be sent. Attach them again.';
        // The chips were cleared before the request and do not come back.
        setNotice(
          message === MENTION_NOT_MEMBER && attachmentIds.length > 0
            ? `${message} Attach the files again.`
            : message
        );
        return;
      }
      showError(
        'Failed to send message',
        getBusinessErrorMessage(error) ?? undefined
      );
    }
  };

  return {
    header,
    messagesQuery,
    messages,
    pending,
    loadError,
    archived,
    canWrite,
    draft,
    setDraft: (value: string) => {
      setChatDraft(channelId, value);
      // An emptied draft forgets its picks, so a typed `@Name` stays text.
      if (value === '') setChatDraftMentions(channelId, []);
    },
    doSend,
    notice,
    setNotice,
    upload,
    offline,
    urls,
    download,
    handleDrop,
    handlePaste,
  };
}
